<template>
  <v-window-item>
    <div v-if="config('fastMenuEntry')" class="black--text font-weight-medium" v-text="name" />
    <v-list>
      <template v-for="(item, i) in items">
        <v-divider
          :key="`d${i}`"
          inset
        />
        <v-list-item
          :key="`i${i}`"
          class="px-1"
          @click="itemClicked(item)"
          @contextmenu="itemLongPressed(item)"
        >
          <v-list-item-action class="my-1">
            <v-badge
              left
              bordered
              :offset-x="item.counter[index] < 10 ? 16 : 22"
              offset-y="16"
              :content="item.counter[index]"
              :value="item.counter[index] > 0"
            >
              <h-avatar :resource="item" />
            </v-badge>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title v-text="item.name" />
          </v-list-item-content>
          <v-fade-transition>
            <v-list-item-action v-if="item.counter[index] > 0">
              <v-menu
                v-if="item.counter[index] > 1 && hasModifiers(item)"
                light
                maxHeight="460"
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="primary"
                    small
                  >
                    <v-icon v-text="icons.minusCircle" />
                  </v-btn>
                </template>
                <v-list subheader>
                  <v-subheader v-text="__('delete')" />
                  <v-list-item
                    v-for="(content, j) in contents.filter(o => o.item_id == item.id)"
                    :key="`i${i}m${j}`"
                    @click="removeItemWithModifiers(item, content)"
                  >
                    <v-list-item-title>{{ content.modifiers.pluck('name').join(', ') || __('withoutModifiers') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item>
                    <v-spacer />
                    <v-btn text color="primary" v-text="__('cancel')" />
                    <v-spacer />
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-btn
                v-else
                icon
                color="primary"
                small
                @click.stop="removeItem(item)"
              >
                <v-icon v-text="icons.minusCircle" />
              </v-btn>
            </v-list-item-action>
          </v-fade-transition>
        </v-list-item>
      </template>
      <v-divider inset />
    </v-list>
    <!-- MODIFIERS -->
    <h-modifiers
      v-model="modifiers"
      @complete="addItemWithModifiers"
    />
  </v-window-item>
</template>

<script>
  import { mapState } from 'vuex'
  import Configurable from '@/Mixins/Configurable'
  import I18n from '@/Mixins/I18n'
  import Lockable from '@/Mixins/Lockable'
  import UsesIcons from '@/Mixins/UsesIcons'
  import MenuChoice from '@/Models/Static/MenuChoice'
  import HAvatar from '@/Components/Avatar'
  import HModifiers from '@/Sections/Order/Modifiers/Dialog'

  export default {
    mixins: [
      Configurable,
      I18n,
      Lockable,
      UsesIcons,
    ],

    components: {
      HAvatar,
      HModifiers,
    },

    props: {
      id: {
        required: true,
        type: Number,
      },
      name: {
        required: true,
        type: String,
      },
      items: {
        required: true,
        type: Array,
      },
    },

    data: () => ({
      MenuChoice,
      modifiers: false,
    }),

    computed: {
      ...mapState('orders/menu', {
        index: 'category',
        editing: 'editing',
        quantity: 'quantity',
        contents: 'contents',
        dishOrder: state => state.categories[state.category].id,
        warnCounter: state => state.warn.counter,
        max (state) {
          return this.MenuChoice.max(state.categories[state.category]) * state.quantity
        },
      }),
      disabled () {
        return this.$store.state.orders.dialogs.locked || this.locked
      },
      isNotLastCategory () {
        return this.$store.getters['orders/menu/isNotLastCategory'](this.index)
      },
      count () {
        return this.$store.getters['orders/menu/countItemsInCategory'](this.index)
      },
    },

    watch: {
      warnCounter (v) {
        if (v >= 3) {
          this.$store.dispatch('orders/menu/stopWarning')
        }
      },
    },

    methods: {
      hasModifiers (item) {
        return item.menuPivot.modifier_group_id || this.$store.getters['catalog/hasVisibleModifiers'](item)
      },
      showModifiers (item) {
        return item.menuPivot.modifier_group_id || this.$store.getters['catalog/showModifiers'](item)
      },
      itemClicked (item) {
        if (this.disabled) {
          return
        }
        this.lock()
        if (this.count >= this.max) {
          this.$store.dispatch('orders/menu/startWarning')
          return
        }
        this.showModifiers(item) ? this.openModifiers(item) : this.addItem(item)
      },
      itemLongPressed (item) {
        if (this.disabled || ! this.hasModifiers(item) || this.count >= this.max) {
          return
        }
        this.openModifiers(item)
      },
      openModifiers (item) {
        const group_id = item.menuPivot.modifier_group_id
        this.$store
          .dispatch('orders/modifiers/init', { item, group_id })
          .then(() => this.modifiers = true)
      },
      addItem (item) {
        this.$store.commit('orders/menu/addContent', {
          item,
          dishOrder: this.dishOrder,
        })
        this.itemAdded(item)
      },
      addItemWithModifiers () {
        const item = this.$store.state.orders.modifiers.item
        this.$store.commit('orders/menu/addContent', {
          item,
          dishOrder: this.dishOrder,
          modifiers: this.$store.getters['orders/modifiers/selected'],
        })
        this.itemAdded(item)
      },
      itemAdded (item) {
        this.$emit('change')
        this.counter('itemIncrement', item.index)
        this.$store.dispatch('orders/menu/validateCategory', this.index)
        if (! this.editing && this.isNotLastCategory && this.count == this.max) {
          this.$store.commit('orders/menu/nextCategory')
          if(this.config('fastMenuEntry')) {
            this.$store.commit('orders/menu/dishOrder', this.index)
          }
        }
      },
      canRemove (item) {
        return item.counter[this.index] > 0 && ! this.disabled
      },
      removeItem (item) {
        if (! this.canRemove(item)) {
          return
        }
        this.lock()
        const contentIndex = this.contents.findIndex(o => o.dishOrder == this.dishOrder && o.item_id == item.id)
        if (contentIndex === -1) {
          return
        }
        this.removeContent(contentIndex, item.index)
      },
      removeItemWithModifiers (item, content) {
        if (! this.canRemove(item)) {
          return
        }
        this.lock()
        const ids = content.modifiers.pluck('id')
        const contentIndex = this.contents.findIndex(o => o.dishOrder == this.dishOrder && o.item_id == item.id && o.modifiers?.pluck('id').isExactly(ids))
        if (contentIndex === -1) {
          return
        }
        setTimeout(() => this.removeContent(contentIndex, item.index), 200)
      },
      removeContent (contentIndex, itemIndex) {
        this.removeFromDatabase(contentIndex)
        this.$store.commit('orders/menu/removeContent', contentIndex)
        this.$emit('change')
        this.counter('itemDecrement', itemIndex)
        this.$store.dispatch('orders/menu/validateCategory', this.index)
      },
      removeFromDatabase (contentIndex) {
        const id = this.contents[contentIndex].id
        if (id) {
          this.$store.commit('orders/removeSubContentFromDatabase', id)
        }
      },
      counter (action, item_index) {
        this.$store.commit(`orders/menu/${action}`, {
          category_index: this.index,
          item_index,
          dishOrder: this.id,
        })
      },
    },
  }
</script>
